import { RouterOutletFade } from "@redotech/react-router-util/outlet-fade";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useTriggerLoad } from "@redotech/react-util/load";
import DotIcon from "@redotech/redo-icons/dot-01.svg";
import ExternalLinkIcon from "@redotech/redo-icons/link-external-02.svg";
import { PopulatedSplit } from "@redotech/redo-storefront-widget-common/split";
import { Team } from "@redotech/redo-team-common/team";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import {
  RedoHorizontalTab,
  RedoHorizontalTabs,
} from "@redotech/redo-web/arbiter-components/tabs/redo-horizontal-tabs";
import { BreadcrumbSlot, useBreadcrumb } from "@redotech/redo-web/breadcrumb";
import { Flex } from "@redotech/redo-web/flex";
import { Action0PortalContext, Actions } from "@redotech/redo-web/page";
import { TabsPortal } from "@redotech/redo-web/tabs-portal";
import { createContext, memo, useContext, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  useMatch,
  useNavigate,
  useParams,
  useResolvedPath,
} from "react-router-dom";
import { RedoAdminClientContext } from "../client/context";
import { EcommerceProviderIntegrationProvider } from "../client/ecommerce-provider-integration-context";
import { getSplits, getTeam } from "../client/team";
import { useMerchantAppLogin } from "./merchant-app";
import * as teamCss from "./team.module.css";
import { PatchAdminSettingsClientProvider } from "./team/patch-admin-settings-client";

const enum TeamTab {
  AEO = "aeo",
  USERS = "users",
  ANALYTICS = "analytics",
  CONVERSION = "conversion",
  COVERAGE = "coverage",
  CUSTOMER_ACCOUNTS = "customer-accounts",
  DISPUTES = "disputes",
  CUSTOMER_SUPPORT = "customer-support",
  BOXLESS_DROPOFFS = "boxless-dropoffs",
  GENERAL = "general",
  PRODUCTS = "products",
  IMS = "ims",
  LANDED_COST = "landed-cost",
  MARKETING = "marketing",
  ORDERS = "orders",
  OUTBOUND_LABELS = "outbound-labels",
  PAYMENTS = "payments",
  RECOGNIZE = "recognize",
  MANAGED_RETURNS = "managed-returns",
  RETURNS_SETTINGS_V2 = "returns",
  MESSAGING = "messaging",
  SHOPPER_AGENT = "shopper-agent",
  TNPL = "tnpl",
  WARRANTIES = "warranties",
}

const teamTabs: RedoHorizontalTab<TeamTab>[] = [
  { label: "General", key: TeamTab.GENERAL },
  { label: "Products", key: TeamTab.PRODUCTS },
  { label: "Users", key: TeamTab.USERS },
  { label: "Returns", key: TeamTab.RETURNS_SETTINGS_V2 },
  { label: "Outbound", key: TeamTab.OUTBOUND_LABELS },
  { label: "Marketing", key: TeamTab.MARKETING },
  { label: "Conversion", key: TeamTab.CONVERSION },
  { label: "AEO", key: TeamTab.AEO },
  { label: "Analytics", key: TeamTab.ANALYTICS },
  { label: "Boxless Drop-offs", key: TeamTab.BOXLESS_DROPOFFS },
  { label: "Cust Accounts", key: TeamTab.CUSTOMER_ACCOUNTS },
  { label: "Disputes", key: TeamTab.DISPUTES },
  { label: "IMS", key: TeamTab.IMS },
  { label: "Landed cost", key: TeamTab.LANDED_COST },
  { label: "Managed Returns", key: TeamTab.MANAGED_RETURNS },
  { label: "Messaging", key: TeamTab.MESSAGING },
  { label: "Orders", key: TeamTab.ORDERS },
  { label: "Payments", key: TeamTab.PAYMENTS },
  { label: "Recognize", key: TeamTab.RECOGNIZE },
  { label: "Shopper Agent", key: TeamTab.SHOPPER_AGENT },
  { label: "Support & AI", key: TeamTab.CUSTOMER_SUPPORT },
  { label: "Try First", key: TeamTab.TNPL },
  { label: "Warranties", key: TeamTab.WARRANTIES },
];

export const TeamContext = createContext<Team | undefined>(undefined);
export const LoadTeamContext = createContext<(() => void) | undefined>(
  undefined,
);

export const TeamPage = memo(function TeamPage({
  breadcrumb,
}: {
  breadcrumb: BreadcrumbSlot;
}) {
  const client = useRequiredContext(RedoAdminClientContext);

  const path = useResolvedPath("");
  const match = useMatch({ path: `${path.pathname}/:child`, end: false });

  const params = useParams();
  const teamId = params.teamId!;

  const [teamLoad, loadTeam] = useTriggerLoad((signal) =>
    getTeam(client, { teamId, signal }),
  );

  const [splitLoad, loadSplit] = useTriggerLoad((signal) =>
    getSplits(client, { teamId, signal }),
  );

  useBreadcrumb(breadcrumb, teamLoad.value?.name || "");

  useEffect(() => {
    loadTeam();
    loadSplit();
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId]);

  const navigate = useNavigate();

  const actions0 = useContext(Action0PortalContext);

  if (!match || !teamLoad.value) {
    return null;
  }

  return (
    <TeamContext.Provider value={teamLoad.value}>
      <EcommerceProviderIntegrationProvider>
        {actions0 &&
          teamLoad.value &&
          createPortal(
            <Actions show>
              <Flex dir="row" gap="md">
                <TreatmentsAction splits={splitLoad.value} />
                <MerchantAppAction team={teamLoad.value} />
              </Flex>
            </Actions>,
            actions0,
          )}

        <div className={teamCss.container}>
          <TabsPortal>
            <RedoHorizontalTabs
              drawBottomLine={false}
              pl="none"
              selectedTab={match.params.child as TeamTab}
              setSelectedTab={(option) => navigate(option)}
              tabs={teamTabs}
            />
          </TabsPortal>
          <PatchAdminSettingsClientProvider>
            <div className={teamCss.container}>
              <LoadTeamContext.Provider value={loadTeam}>
                <RouterOutletFade containerClassName={teamCss.tabOutlet} />
              </LoadTeamContext.Provider>
            </div>
          </PatchAdminSettingsClientProvider>
        </div>
      </EcommerceProviderIntegrationProvider>
    </TeamContext.Provider>
  );
});

const MerchantAppAction = memo(function MerchantAppAction({
  team,
}: {
  team: Team;
}) {
  const merchantAppLogin = useMerchantAppLogin();

  const handleClick = useHandler(() => {
    merchantAppLogin(team._id);
  });

  return (
    <RedoButton
      hierarchy="secondary"
      IconTrailing={ExternalLinkIcon}
      onClick={handleClick}
      size="sm"
      text="Merchant dashboard"
    />
  );
});

const TreatmentsAction = memo(function TreatmentsAction({
  splits,
}: {
  splits: PopulatedSplit[] | undefined;
}) {
  const navigate = useNavigate();

  const hasActiveSplit = splits?.some((split) => split.active);

  return (
    <RedoButton
      hierarchy="secondary"
      IconLeading={
        hasActiveSplit
          ? () => <DotIcon className={teamCss.splitStatus} />
          : undefined
      }
      onClick={() => navigate("split")}
      size="sm"
      text="A/B testing"
    />
  );
});
